import React from 'react';
import { format } from 'date-fns';
import { de, enGB, enUS, es, fr, it, ru, nb } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { getCurrentLocale, Locale } from '../../context/I18n';
import type { Locale as DateFnsLocale } from 'date-fns';
import type { Moment } from 'moment';

export type DateFormat =
  | 'day'
  | 'dayname'
  | 'short'
  | 'long'
  | 'compact'
  | 'day-with-month'
  | 'day-with-weekday';

type Props = {
  day: string | Date | Moment;
  format?: DateFormat;
  className?: string;
};

const supportedDateFnsLocales: Record<Locale, DateFnsLocale> = {
  en: enGB,
  'en-us': enUS,
  'de-ch': de,
  de,
  fr,
  it,
  es,
  ru,
  no: nb,
};

const getDateFnsLocale = (locale: Locale): DateFnsLocale => {
  return supportedDateFnsLocales[locale] || enGB;
};

const parseDate = (day: string | Date | Moment): Date => {
  if (day instanceof Date) {
    return day;
  }
  if (
    typeof day === 'object' &&
    day !== null &&
    'toDate' in day &&
    typeof day.toDate === 'function'
  ) {
    return day.toDate();
  }
  if (typeof day === 'string') {
    return new Date(day);
  }
  return new Date();
};

const FormattedDate: React.FC<Props> = ({
  day,
  format: formatType = 'long',
  className,
}) => {
  const { t } = useTranslation();
  const formatDate = formattedDate(t);

  return (
    <span className={className}>{formatDate({ day, format: formatType })}</span>
  );
};

export const formattedDate =
  (t: TFunction) =>
  ({ day, format: formatType }: Props) => {
    const formatString = t(`dateFnsFormat.date.${formatType}`);
    const locale = getCurrentLocale();
    const dateFnsLocale = getDateFnsLocale(locale);

    try {
      return format(parseDate(day), formatString, { locale: dateFnsLocale });
    } catch (error) {
      console.error('Error formatting date:', error, {
        day,
        formatType,
        formatString,
      });
      return String(day);
    }
  };

export default FormattedDate;
